<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { Pencil } from 'lucide-vue-next'
import Button from 'pejer/src/components/ui/button/Button.vue'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'pejer/src/components/ui/form'
import { toast } from 'pejer/src/components/ui/toast'
import { Users } from 'pejer/src/tables/Users'
import usePejerMutation from 'pejer/src/usePejerMutation'
import { Form } from 'vee-validate'
import { ref } from 'vue'
import { z } from 'zod'
import { usePejerBase } from '../../stores/pejerData'
import { PlatformUserRoles } from '../../types/Roles'
import DataStateButton from '../DataState/DataStateButton.vue'
import ProtectedButton from '../ProtectedButton.vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

const props = defineProps<{
  user: Users.Full
}>()

const formObject = z.object({
  roles: z.array(
    z
      .enum([
        PlatformUserRoles.Admin,
        PlatformUserRoles.FinancialAdmin,
        PlatformUserRoles.CustomerSupport,
        PlatformUserRoles.Auditor,
      ])
      .default(PlatformUserRoles.CustomerSupport)
  ),
})

const formSchema = toTypedSchema(formObject)

const pejer = usePejerBase()
const modalOpen = ref(false)

const { mutateAsync, dataState, isPending } = usePejerMutation(pejer.urls.users.edit, {
  queryKeys: [['users']],
  onSuccess: () => {
    toast({
      title: 'User edited successfully',
    })
    modalOpen.value = false
  },
})

const editUser = async (values: z.infer<typeof formObject>) => {
  await mutateAsync({
    pathArgs: {
      id: props.user.userId,
    },
    body: {
      roles: values.roles,
    },
  })
}
</script>
<template>
  <div class="flex justify-between shrink-0">
    <Dialog v-model:open="modalOpen">
      <DialogTrigger class="flex justify-between">
        <ProtectedButton :url="pejer.urls.users.edit" variant="ghost" class="p-2">
          <Pencil :size="14" />
        </ProtectedButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            <b>{{ user.email }}</b> will be able to view and act on this platforms data, assign an
            appropriate role representing their responsibilities.
          </DialogDescription>
        </DialogHeader>

        <Form
          @submit="editUser"
          :initial-values="{ roles: props.user?.roles }"
          class="w-full justify-center flex flex-col gap-4"
          :validation-schema="formSchema"
        >
          <div class="w-full justify-center flex flex-col gap-4 mb-4">
            <FormField v-slot="{ componentField }" name="roles[0]">
              <FormItem>
                <FormLabel class="text-secondaryBlack">Role</FormLabel>
                <FormControl>
                  <Select
                    v-bind="componentField"
                    :default-value="PlatformUserRoles.CustomerSupport"
                    :disabled="componentField.modelValue === PlatformUserRoles.Owner || isPending"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Owner" disabled>Owner</SelectItem>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="FinancialAdmin">Financial Admin</SelectItem>
                      <SelectItem value="CustomerSupport">Customer Support</SelectItem>
                      <SelectItem value="Auditor">Auditor</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription v-if="componentField.modelValue === PlatformUserRoles.Owner">
                  Cannot change the role of the owner
                </FormDescription>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>

          <DialogFooter>
            <DialogClose as-child>
              <Button :disabled="isPending" type="button" variant="outline"> Cancel </Button>
            </DialogClose>
            <DataStateButton type="submit" variant="default" :data-state="dataState">
              Edit
            </DataStateButton>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  </div>
</template>
